import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import {
    Add,
    CheckCircle,
    Close,
    ContentCopy,
    Delete,
    Description,
    Preview,
    Save,
} from "@mui/icons-material";

import { Alert, Autocomplete, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, MenuItem, Paper, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import CreateCustomer from "../../../customer/CreateCustomer";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";

import {
    COLORS,
    cardSx,
} from "../shared/FinanceUiKit";
import { toLocalDateInputValue } from "../shared/dateInput";
import { appConfirm, appAlert } from "components/AppDialog";
import { AddRowButton, RemoveRowButton } from "components/RowActions";
// Koyu hero bandı için (yedekten geri yüklendi)
import { ReceiptLong } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const currencyOptions = [
    "EUR",
    "USD",
    "TRY",
    "GBP",
    "CHF",
];

// Turkish VAT rates currently in force. 8% and 18% were replaced by 10% and
// 20% in July 2023, so they are only offered for purchase invoices (older
// supplier documents may still carry them) or when an existing invoice already
// stores one of them.
const vatOptions = [
    0,
    1,
    10,
    20,
];

const legacyPurchaseVatOptions = [
    0,
    1,
    8,
    10,
    18,
    20,
];

const getVatOptionsFor = (invoiceType, currentRate) => {
    const base =
        invoiceType === "purchase"
            ? legacyPurchaseVatOptions
            : vatOptions;
    const current = Number(currentRate);

    return Number.isFinite(current) && !base.includes(current)
        ? [...base, current].sort((a, b) => a - b)
        : base;
};

const invoiceTypeOptions = [
    {
        value: "sales",
        label: "Sales Invoice",
    },
    {
        value: "purchase",
        label: "Purchase Invoice",
    },
];

const COLLECTION_RISK_STATUSES = new Set([
    "normal",
    "overdue",
    "doubtful",
    "bad_debt",
    "written_off",
]);

const normalizeRiskStatus = (value) => {
    const normalized = String(value || "normal")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

    const aliases = {
        watch: "overdue",
        high: "doubtful",
        high_risk: "doubtful",
        highrisk: "doubtful",
        legal: "bad_debt",
        write_off: "written_off",
        writeoff: "written_off",
    };

    const resolvedStatus =
        aliases[normalized] || normalized;

    return COLLECTION_RISK_STATUSES.has(
        resolvedStatus,
    )
        ? resolvedStatus
        : "normal";
};

const normalizeCollectionRisk = (invoice = {}) => {
    const risk =
        invoice?.collectionRisk ||
        invoice?.collection_risk ||
        {};

    return {
        ...risk,
        status: normalizeRiskStatus(
            risk?.status ||
            invoice?.riskStatus ||
            invoice?.risk_status ||
            "normal",
        ),
        reason: risk?.reason || "",
        notes: risk?.notes || "",
        expectedRecoveryAmount: toNumber(
            risk?.expectedRecoveryAmount ??
            risk?.expected_recovery_amount,
        ),
        provisionAmount: toNumber(
            risk?.provisionAmount ??
            risk?.provision_amount,
        ),
        writtenOffAmount: toNumber(
            risk?.writtenOffAmount ??
            risk?.written_off_amount,
        ),
        updatedAt:
            risk?.updatedAt ||
            risk?.updated_at ||
            null,
        updatedBy:
            risk?.updatedBy ||
            risk?.updated_by ||
            null,
    };
};

const NEW_CUSTOMER_OPTION = {
    id: "new-account",
    _id: "new-account",
    name: "+ New Account",
    type: "new",
};

const expenseCenterOptions = [
    "GENERAL EXPENSES",
    "OCEAN OPERATIONS",
    "ROAD OPERATIONS",
    "AIR OPERATIONS",
    "WAREHOUSING",
    "CUSTOMS OPERATIONS",
    "ADMINISTRATIVE EXPENSES",
];

const operationOptions = [
    "GENERAL",
    "OCEAN",
    "ROAD",
    "AIR",
    "WAREHOUSING",
    "CUSTOMS",
];

const invoiceItemOptions = [
    {
        code: "FREIGHT",
        label: "Freight Charge",
        description: "Freight service",
    },
    {
        code: "PICKUP",
        label: "Pickup Charge",
        description: "Cargo pickup service",
    },
    {
        code: "DELIVERY",
        label: "Delivery Charge",
        description: "Cargo delivery service",
    },
    {
        code: "CUSTOMS",
        label: "Customs Clearance",
        description: "Customs clearance service",
    },
    {
        code: "HANDLING",
        label: "Handling Charge",
        description: "Cargo handling service",
    },
    {
        code: "DOCUMENTATION",
        label: "Documentation Fee",
        description: "Documentation service",
    },
    {
        code: "STORAGE",
        label: "Storage Charge",
        description: "Cargo storage service",
    },
    {
        code: "DEMURRAGE",
        label: "Demurrage",
        description: "Demurrage charge",
    },
    {
        code: "DETENTION",
        label: "Detention",
        description: "Detention charge",
    },
    {
        code: "INSURANCE",
        label: "Insurance",
        description: "Cargo insurance service",
    },
    {
        code: "OTHER",
        label: "Other Service",
        description: "Other logistics service",
    },
];

const normalizeCurrency = (
    value,
    fallback = "EUR",
) => {
    const normalized = String(
        value || fallback,
    )
        .trim()
        .toUpperCase();

    if (normalized === "EURO") {
        return "EUR";
    }

    if (normalized === "TL") {
        return "TRY";
    }

    return normalized || fallback;
};

const toNumber = (value) => {
    const parsed = Number(value);

    return Number.isFinite(parsed)
        ? parsed
        : 0;
};

const getTodayValue = () =>
    toLocalDateInputValue();

const getDefaultDueDate = () => {
    const date = new Date();

    date.setDate(date.getDate() + 30);

    return toLocalDateInputValue(date);
};

const createEmptyInvoiceItem = (
    sellingCurrency = "EUR",
    costCurrency = "EUR",
) => ({
    itemCode: "",
    commodity: "",
    description: "",
    expenseCenter: "",

    unitQuantity: 1,

    unitPrice: 0,
    sellingRateCurrency:
        normalizeCurrency(sellingCurrency),

    totalCost: 0,
    costCurrency:
        normalizeCurrency(costCurrency),

    vatRate: 0,
    amount: 0,
    vatAmount: 0,
    totalWithVat: 0,
});

const calculateInvoiceItem = (item) => {
    const unitQuantity =
        toNumber(item?.unitQuantity) || 1;

    const unitPrice =
        toNumber(item?.unitPrice);

    const totalCost =
        toNumber(item?.totalCost);

    const vatRate =
        toNumber(item?.vatRate);

    const amount =
        unitQuantity * unitPrice;

    const vatAmount =
        amount * (vatRate / 100);

    return {
        ...item,

        unitQuantity,
        unitPrice,
        totalCost,
        vatRate,

        sellingRateCurrency:
            normalizeCurrency(
                item?.sellingRateCurrency,
            ),

        costCurrency:
            normalizeCurrency(
                item?.costCurrency,
            ),

        amount,
        vatAmount,

        totalWithVat:
            amount + vatAmount,
    };
};

const initialFormState = {
    invoiceOrigin: "manual",
    invoiceType: "sales",
    transportMode: "none",
    sourceType: "",

    invoiceNo: "",

    customerId: "",
    customerName: "",

    supplierId: "",
    supplierName: "",

    email: "",

    invoiceDate: getTodayValue(),
    dueDate: getDefaultDueDate(),

    currency: "EUR",
    sellingRateCurrency: "EUR",
    costCurrency: "EUR",
    totalCostCurrency: "EUR",

    exchangeRate: 1,

    expenseCenter: "",
    branch: "",
    operation: "",
    salesRepresentative: "",

    invoiceAddress: "",
    notes: "",

    exemptionCode: "",
    exemptionReason: "",

    discountAmount: 0,

    invoiceStatus: "Draft",
    approvalStatus: "Pending",

    collectionRisk: {
        status: "normal",
        reason: "",
        notes: "",
        expectedRecoveryAmount: 0,
        provisionAmount: 0,
        writtenOffAmount: 0,
        updatedAt: null,
        updatedBy: null,
    },
    riskStatus: "normal",

    invoiceItems: [
        createEmptyInvoiceItem(),
    ],
};

const extractRows = (payload) => {
    const candidates = [
        payload,
        payload?.data,
        payload?.records,
        payload?.results,
        payload?.customers,
        payload?.suppliers,
        payload?.serviceProviders,
        payload?.globalAgents,
        payload?.global_agents,
        payload?.data?.records,
        payload?.data?.results,
        payload?.data?.customers,
        payload?.data?.suppliers,
        payload?.data?.serviceProviders,
        payload?.data?.globalAgents,
        payload?.data?.global_agents,
    ];

    return (
        candidates.find(Array.isArray) ||
        []
    );
};

const extractRecord = (payload) =>
    payload?.data?.invoice ||
    payload?.data?.data ||
    payload?.invoice ||
    payload?.data ||
    payload;

const getPartyId = (party) =>
    party?._id ||
    party?.id ||
    party?.attributes?._id ||
    party?.attributes?.id ||
    party?.customerId ||
    party?.supplierId ||
    party?.serviceProviderId ||
    "";

const getPartyName = (party) =>
    party?.companyName ||
    party?.customerName ||
    party?.supplierName ||
    party?.serviceProviderName ||
    party?.name ||
    party?.nameKey ||
    party?.attributes?.companyName ||
    party?.attributes?.customerName ||
    party?.attributes?.supplierName ||
    party?.attributes?.serviceProviderName ||
    party?.attributes?.name ||
    party?.attributes?.nameKey ||
    "";

const getPartyEmail = (party) =>
    party?.email ||
    party?.accountingEmail ||
    party?.financeEmail ||
    party?.contactEmail ||
    party?.attributes?.email ||
    party?.attributes?.accountingEmail ||
    party?.attributes?.financeEmail ||
    party?.attributes?.contactEmail ||
    "";

const getPartyAddress = (party) =>
    party?.invoiceAddress ||
    party?.address ||
    party?.companyAddress ||
    party?.fullAddress ||
    party?.attributes?.invoiceAddress ||
    party?.attributes?.address ||
    party?.attributes?.companyAddress ||
    party?.attributes?.fullAddress ||
    "";

const getPartyType = (party) =>
    party?.partyType ||
    party?.type ||
    party?.attributes?.partyType ||
    "customer";

const getPartyTypeLabel = (party) => {
    const type = String(
        getPartyType(party),
    ).toLowerCase();

    if (
        type === "serviceprovider" ||
        type === "service-provider"
    ) {
        return "Service Provider";
    }

    if (
        type === "globalagent" ||
        type === "global-agent" ||
        type === "agent"
    ) {
        return "Global Agent";
    }

    return "Customer";
};

const normalizeParty = (
    party,
    type,
) => ({
    ...party,
    partyType: type,
    type,
});

const formatMoney = (
    value,
    currency = "EUR",
) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: normalizeCurrency(currency),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(toNumber(value));

function CreateManualInvoice() {
    const navigate = useNavigate();

    const {
        invoiceId,
    } = useParams();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const token =
        localStorage.getItem("token");

    const [form, setForm] =
        useState(initialFormState);

    const [customers, setCustomers] =
        useState([]);

    const [suppliers, setSuppliers] =
        useState([]);

    const [
        salesRepresentativeOptions,
        setSalesRepresentativeOptions,
    ] = useState([]);

    const [
        selectedCustomer,
        setSelectedCustomer,
    ] = useState(null);

    const [
        customerInputValue,
        setCustomerInputValue,
    ] = useState("");

    const [
        customerOptionsOpen,
        setCustomerOptionsOpen,
    ] = useState(false);

    const [
        customerModalOpen,
        setCustomerModalOpen,
    ] = useState(false);

    const [
        selectedSupplier,
        setSelectedSupplier,
    ] = useState(null);

    const [loading, setLoading] =
        useState(false);

    const [
        loadingParties,
        setLoadingParties,
    ] = useState(false);

    const [
        loadingInvoice,
        setLoadingInvoice,
    ] = useState(false);

    const [
        previewOpen,
        setPreviewOpen,
    ] = useState(false);

    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");

    const [
        successMessage,
        setSuccessMessage,
    ] = useState("");

    const isEditMode = Boolean(invoiceId);

    // Issued / Received invoices keep their lines, amounts, discount and
    // currencies frozen (the API answers 409 otherwise) — lock those inputs
    // instead of letting the user edit them and fail on save.
    const itemsLocked =
        isEditMode &&
        ["Issued", "Received"].includes(form.invoiceStatus);

    const fetchWithAuth = useCallback(
        async (
            path,
            options = {},
        ) => {
            const response = await fetch(
                `${API_URL}${path}`,
                {
                    ...options,

                    headers: {
                        Authorization:
                            `Bearer ${token}`,

                        ...(options.body
                            ? {
                                "Content-Type":
                                    "application/json",
                            }
                            : {}),

                        ...options.headers,
                    },
                },
            );

            const payload =
                await response
                    .json()
                    .catch(() => null);

            if (!response.ok) {
                throw new Error(
                    payload?.message ||
                    `${path} request failed.`,
                );
            }

            return payload;
        },
        [token],
    );

    const loadFirstAvailable = useCallback(
        async (paths) => {
            for (const path of paths) {
                try {
                    const payload =
                        await fetchWithAuth(path);

                    return extractRows(payload);
                } catch (error) {
                    console.warn(
                        `${path} request failed`,
                        error,
                    );
                }
            }

            return [];
        },
        [fetchWithAuth],
    );

    const loadParties = useCallback(
        async () => {
            try {
                setLoadingParties(true);

                const [
                    customerRows,
                    supplierRows,
                    globalAgentRows,
                ] = await Promise.all([
                    loadFirstAvailable([
                        "/customer",
                        "/customers",
                    ]),

                    loadFirstAvailable([
                        "/serviceProvider",
                        "/service-provider",
                        "/finance/suppliers",
                        "/suppliers",
                    ]),

                    loadFirstAvailable([
                        "/globalAgent",
                        "/global-agent",
                        "/global-agents",
                    ]),
                ]);

                setCustomers(
                    [
                        ...customerRows.map(
                            (customer) =>
                                normalizeParty(
                                    customer,
                                    "customer",
                                ),
                        ),
                        ...supplierRows.map(
                            (serviceProvider) =>
                                normalizeParty(
                                    serviceProvider,
                                    "serviceProvider",
                                ),
                        ),
                        ...globalAgentRows.map(
                            (globalAgent) =>
                                normalizeParty(
                                    globalAgent,
                                    "globalAgent",
                                ),
                        ),
                    ]
                        .filter((party) =>
                            Boolean(
                                getPartyName(
                                    party,
                                ),
                            ),
                        )
                        .sort((a, b) =>
                            getPartyName(a).localeCompare(
                                getPartyName(b),
                            ),
                        ),
                );

                setSuppliers(
                    supplierRows
                        .map((supplier) =>
                            normalizeParty(
                                supplier,
                                "serviceProvider",
                            ),
                        )
                        .filter((supplier) =>
                            Boolean(
                                getPartyName(
                                    supplier,
                                ),
                            ),
                        )
                        .sort((a, b) =>
                            getPartyName(a).localeCompare(
                                getPartyName(b),
                            ),
                        ),
                );
            } catch (error) {
                console.error(
                    "Business partner load error:",
                    error,
                );

                setErrorMessage(
                    financeText("Customers and suppliers could not be loaded."),
                );
            } finally {
                setLoadingParties(false);
            }
        },
        [loadFirstAvailable],
    );

    useEffect(() => {
        let active = true;

        const loadSalesRepresentatives = async () => {
            try {
                const response = await CrudService.getUsers();
                const rows = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.data)
                        ? response.data.data
                        : [];

                const options = rows
                    .map((user) => ({
                        id: user?.id || user?._id,
                        label:
                            user?.attributes?.name ||
                            user?.name ||
                            user?.fullName ||
                            user?.email ||
                            "",
                    }))
                    .filter((user) => user.label)
                    .sort((a, b) =>
                        a.label.localeCompare(b.label),
                    );

                if (active) {
                    setSalesRepresentativeOptions(options);
                }
            } catch (error) {
                console.error(
                    "Sales representatives could not be loaded:",
                    error,
                );

                if (active) {
                    setSalesRepresentativeOptions([]);
                }
            }
        };

        loadSalesRepresentatives();

        return () => {
            active = false;
        };
    }, []);

    const normalizeLoadedInvoice = useCallback(
        (invoice) => {
            const sellingCurrency =
                normalizeCurrency(
                    invoice?.sellingRateCurrency ||
                    invoice?.selling_rate_currency ||
                    invoice?.currency ||
                    "EUR",
                );

            const costCurrency =
                normalizeCurrency(
                    invoice?.costCurrency ||
                    invoice?.cost_currency ||
                    invoice?.totalCostCurrency ||
                    invoice?.total_cost_currency ||
                    "EUR",
                );

            const sourceItems = [
                invoice?.invoiceItems,
                invoice?.invoice_items,
                invoice?.cargoDetails,
                invoice?.cargo_details,
            ].find(Array.isArray) || [];

            const invoiceItems =
                sourceItems.length
                    ? sourceItems.map(
                        (sourceItem) => {
                            const item =
                                sourceItem?.attributes ||
                                sourceItem;

                            return calculateInvoiceItem({
                                itemCode:
                                    item?.itemCode ||
                                    item?.item_code ||
                                    "",

                                commodity:
                                    item?.commodity ||
                                    item?.invoiceItem ||
                                    item?.invoice_item ||
                                    "",

                                description:
                                    item?.description ||
                                    "",

                                expenseCenter:
                                    item?.expenseCenter ||
                                    item?.expense_center ||
                                    "",

                                unitQuantity:
                                    item?.unitQuantity ??
                                    item?.unit_quantity ??
                                    item?.quantity ??
                                    1,

                                unitPrice:
                                    item?.unitPrice ??
                                    item?.unit_price ??
                                    item?.sellingRate ??
                                    item?.selling_rate ??
                                    0,

                                sellingRateCurrency:
                                    item?.sellingRateCurrency ||
                                    item?.selling_rate_currency ||
                                    item?.currency ||
                                    sellingCurrency,

                                totalCost:
                                    item?.totalCost ??
                                    item?.total_cost ??
                                    item?.cost ??
                                    0,

                                costCurrency:
                                    item?.costCurrency ||
                                    item?.cost_currency ||
                                    costCurrency,

                                vatRate:
                                    item?.vatRate ??
                                    item?.vat_rate ??
                                    0,
                            });
                        },
                    )
                    : [
                        createEmptyInvoiceItem(
                            sellingCurrency,
                            costCurrency,
                        ),
                    ];

            const collectionRisk =
                normalizeCollectionRisk(invoice);

            return {
                ...initialFormState,
                ...invoice,

                invoiceOrigin: "manual",

                invoiceType:
                    invoice?.invoiceType ===
                    "purchase"
                        ? "purchase"
                        : "sales",

                transportMode: "none",
                sourceType: "",

                invoiceNo:
                    invoice?.invoiceNo ||
                    invoice?.invoice_no ||
                    "",

                customerId:
                    invoice?.customerId ||
                    "",

                customerName:
                    invoice?.customerName ||
                    "",

                supplierId:
                    invoice?.supplierId ||
                    "",

                supplierName:
                    invoice?.supplierName ||
                    "",

                email:
                    invoice?.email ||
                    "",

                invoiceDate:
                    invoice?.invoiceDate
                        ? new Date(
                            invoice.invoiceDate,
                        )
                            .toISOString()
                            .slice(0, 10)
                        : getTodayValue(),

                dueDate:
                    invoice?.dueDate
                        ? new Date(
                            invoice.dueDate,
                        )
                            .toISOString()
                            .slice(0, 10)
                        : "",

                currency:
                sellingCurrency,

                sellingRateCurrency:
                sellingCurrency,

                costCurrency,

                totalCostCurrency:
                costCurrency,

                exchangeRate:
                    toNumber(
                        invoice?.exchangeRate,
                    ) || 1,

                expenseCenter:
                    invoice?.expenseCenter ||
                    "",

                branch:
                    invoice?.branch ||
                    "",

                operation:
                    invoice?.operation ||
                    "",

                salesRepresentative:
                    invoice?.salesRepresentative ||
                    "",

                invoiceAddress:
                    invoice?.invoiceAddress ||
                    "",

                notes:
                    invoice?.notes ||
                    "",

                discountAmount:
                    toNumber(
                        invoice?.discountAmount,
                    ),

                collectionRisk,
                riskStatus:
                collectionRisk.status,

                invoiceItems,
            };
        },
        [],
    );

    const loadInvoice = useCallback(
        async () => {
            if (!invoiceId) {
                return;
            }

            try {
                setLoadingInvoice(true);

                const payload =
                    await fetchWithAuth(
                        `/finance/manual-invoices/${invoiceId}`,
                    );

                const invoice =
                    extractRecord(payload);

                if (
                    !invoice ||
                    typeof invoice !== "object"
                ) {
                    throw new Error(
                        financeText("Manual invoice could not be found."),
                    );
                }

                const normalized =
                    normalizeLoadedInvoice(invoice);

                setForm(normalized);
            } catch (error) {
                console.error(
                    "Manual invoice detail error:",
                    error,
                );

                setErrorMessage(
                    error.message ||
                    financeText("Manual invoice could not be loaded."),
                );
            } finally {
                setLoadingInvoice(false);
            }
        },
        [
            fetchWithAuth,
            invoiceId,
            normalizeLoadedInvoice,
        ],
    );

    useEffect(() => {
        loadParties();
    }, [loadParties]);

    useEffect(() => {
        loadInvoice();
    }, [loadInvoice]);

    const [exemptionCodes, setExemptionCodes] = useState({
        fullExemption: [],
        partialExemption: [],
    });

    useEffect(() => {
        let active = true;

        const loadExemptionCodes = async () => {
            try {
                const result = await fetchWithAuth("/edm/exemption-codes");

                if (active && result?.success && result.data) {
                    setExemptionCodes(result.data);
                }
            } catch (error) {
                console.error("Exemption code list load error:", error);
            }
        };

        loadExemptionCodes();

        return () => {
            active = false;
        };
    }, [fetchWithAuth]);

    const exemptionOptions = useMemo(
        () => [
            ...(exemptionCodes.fullExemption || []),
            ...(exemptionCodes.partialExemption || []),
        ],
        [exemptionCodes],
    );

    // ------------------------------------------------------------------
    // Foreign invoice PDF: attach the PDF and mail it to accounting
    // ------------------------------------------------------------------
    const [pdfFile, setPdfFile] = useState(null);
    const [sendPdfMail, setSendPdfMail] = useState(true);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [pdfNotice, setPdfNotice] = useState("");
    const [pdfMailConfig, setPdfMailConfig] = useState({
        recipients: [],
        maxSizeMb: 15,
    });

    useEffect(() => {
        let active = true;

        const loadPdfMailConfig = async () => {
            try {
                const result = await fetchWithAuth(
                    "/finance/manual-invoices/pdf-mail/config",
                );

                if (active && result?.data) {
                    setPdfMailConfig(result.data);
                }
            } catch (error) {
                console.error("PDF mail config load error:", error);
            }
        };

        loadPdfMailConfig();

        return () => {
            active = false;
        };
    }, [fetchWithAuth]);

    const uploadInvoicePdf = async (targetId, file) => {
        const body = new FormData();
        body.append("file", file);

        const response = await fetch(
            `${API_URL}/finance/manual-invoices/${targetId}/pdf`,
            {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body,
            },
        );

        const payload = await response.json().catch(() => null);

        if (!response.ok) {
            throw new Error(
                payload?.message || financeText("The PDF could not be uploaded."),
            );
        }

        return payload?.data || null;
    };

    const mailInvoicePdf = async (targetId, resend = false) =>
        fetchWithAuth(`/finance/manual-invoices/${targetId}/send-pdf`, {
            method: "POST",
            body: JSON.stringify({ resend }),
        });

    const applyPdfInfo = (info) =>
        setForm((previous) => ({
            ...previous,
            foreignInvoicePdf: info?.pdf || undefined,
            foreignInvoiceMail: info?.mail || undefined,
        }));

    const handlePdfChosen = (event) => {
        const file = event.target.files?.[0] || null;

        event.target.value = "";

        if (!file) return;

        if (
            file.type !== "application/pdf" &&
            !file.name.toLowerCase().endsWith(".pdf")
        ) {
            setErrorMessage(financeText("Only PDF files can be uploaded."));
            return;
        }

        if (file.size > (pdfMailConfig.maxSizeMb || 15) * 1024 * 1024) {
            setErrorMessage(
                `${financeText("The PDF is larger than")} ${pdfMailConfig.maxSizeMb || 15} MB.`,
            );
            return;
        }

        setErrorMessage("");
        setPdfFile(file);
    };

    const viewSavedPdf = async () => {
        try {
            setPdfBusy(true);

            const response = await fetch(
                `${API_URL}/finance/manual-invoices/${invoiceId}/pdf`,
                { headers: { Authorization: `Bearer ${token}` } },
            );

            if (!response.ok) {
                throw new Error(financeText("PDF not found."));
            }

            const blobUrl = URL.createObjectURL(await response.blob());

            window.open(blobUrl, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setPdfBusy(false);
        }
    };

    const removeSavedPdf = async () => {
        if (!await appConfirm(financeText("Remove the attached PDF?"))) return;

        try {
            setPdfBusy(true);

            await fetchWithAuth(`/finance/manual-invoices/${invoiceId}/pdf`, {
                method: "DELETE",
            });

            applyPdfInfo(null);
            setPdfNotice("");
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setPdfBusy(false);
        }
    };

    const sendSavedPdf = async (resend) => {
        try {
            setPdfBusy(true);
            setErrorMessage("");

            if (pdfFile) {
                applyPdfInfo(await uploadInvoicePdf(invoiceId, pdfFile));
                setPdfFile(null);
            }

            const result = await mailInvoicePdf(invoiceId, resend);

            applyPdfInfo(result?.data);
            setPdfNotice(result?.message || "");
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setPdfBusy(false);
        }
    };

    // 0% VAT lines on a sales invoice go to EDM as "İstisna" and need a legal
    // exemption reason; taxable lines do not.
    const hasZeroVatLine =
        form.invoiceType === "sales" &&
        form.invoiceItems.some(
            (item) => toNumber(item.vatRate) === 0,
        );

    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        if (
            form.customerId &&
            customers.length
        ) {
            const customer =
                customers.find(
                    (item) =>
                        String(getPartyId(item)) ===
                        String(form.customerId),
                );

            setSelectedCustomer(
                customer || null,
            );
            // Fall back to the name stored on the invoice when the account
            // can no longer be found in the customer list.
            setCustomerInputValue(
                getPartyName(customer) ||
                form.customerName ||
                "",
            );
        } else if (
            !form.customerId &&
            form.customerName
        ) {
            setCustomerInputValue(
                form.customerName,
            );
        }

        if (
            form.supplierId &&
            suppliers.length
        ) {
            const supplier =
                suppliers.find(
                    (item) =>
                        String(getPartyId(item)) ===
                        String(form.supplierId),
                );

            setSelectedSupplier(
                supplier ||
                (form.supplierName
                    ? {
                        _id: form.supplierId,
                        supplierName:
                            form.supplierName,
                    }
                    : null),
            );
        }
    }, [
        customers,
        suppliers,
        form.customerId,
        form.customerName,
        form.supplierId,
        form.supplierName,
        isEditMode,
    ]);

    const totals = useMemo(() => {
        const normalizedItems =
            form.invoiceItems.map(
                calculateInvoiceItem,
            );

        const subtotal =
            normalizedItems.reduce(
                (sum, item) =>
                    sum +
                    toNumber(item.amount),
                0,
            );

        const vatTotal =
            normalizedItems.reduce(
                (sum, item) =>
                    sum +
                    toNumber(item.vatAmount),
                0,
            );

        const totalCost =
            normalizedItems.reduce(
                (sum, item) =>
                    sum +
                    toNumber(item.totalCost),
                0,
            );

        const discountAmount =
            toNumber(
                form.discountAmount,
            );

        const invoiceTotal =
            Math.max(
                subtotal +
                vatTotal -
                discountAmount,
                0,
            );

        return {
            subtotal,
            vatTotal,
            totalCost,
            discountAmount,
            invoiceTotal,
        };
    }, [
        form.invoiceItems,
        form.discountAmount,
    ]);

    // Invoice preview line items, shown with the shared DataTable (same
    // component as the other finance screens) instead of a hand-built table.
    const previewTable = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("Item"),
                    accessor: "commodity",
                    width: "22%",
                },
                {
                    Header: financeText("Description"),
                    accessor: "description",
                },
                {
                    Header: financeText("Quantity"),
                    accessor: "unitQuantity",
                    align: "right",
                },
                {
                    Header: financeText("Unit Price"),
                    accessor: "unitPrice",
                    align: "right",
                    Cell: ({ row }) =>
                        formatMoney(
                            row.original.unitPrice,
                            row.original.sellingRateCurrency,
                        ),
                },
                {
                    Header: financeText("VAT"),
                    accessor: "vatRate",
                    align: "right",
                    Cell: ({ value }) => `${value}%`,
                },
                {
                    Header: financeText("Amount"),
                    accessor: "totalWithVat",
                    align: "right",
                    Cell: ({ row }) =>
                        formatMoney(
                            row.original.totalWithVat,
                            row.original.sellingRateCurrency,
                        ),
                },
            ],
            rows: form.invoiceItems.map(calculateInvoiceItem),
        }),
        [form.invoiceItems, financeLanguage],
    );

    const updateFormField = (
        field,
        value,
    ) => {
        setForm((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleInvoiceTypeChange = (
        event,
    ) => {
        const invoiceType =
            event.target.value;

        setSelectedCustomer(null);
        setCustomerInputValue("");
        setCustomerOptionsOpen(false);
        setSelectedSupplier(null);

        setForm((previous) => ({
            ...previous,

            invoiceType,

            customerId: "",
            customerName: "",

            supplierId: "",
            supplierName: "",

            email: "",
            invoiceAddress: "",
        }));
    };

    const handleCustomerChange = (
        event,
        customer,
    ) => {
        setSelectedCustomer(customer);
        setCustomerInputValue(
            getPartyName(customer),
        );

        setForm((previous) => ({
            ...previous,

            customerId:
                getPartyId(customer),

            customerName:
                getPartyName(customer),

            email:
                getPartyEmail(customer),

            invoiceAddress:
                getPartyAddress(customer),
        }));
    };

    const handleSupplierChange = (
        event,
        supplier,
    ) => {
        setSelectedSupplier(supplier);

        setForm((previous) => ({
            ...previous,

            supplierId:
                getPartyId(supplier),

            supplierName:
                getPartyName(supplier),

            email:
                getPartyEmail(supplier),

            invoiceAddress:
                getPartyAddress(supplier),
        }));
    };

    const updateInvoiceItem = (
        index,
        field,
        value,
    ) => {
        setForm((previous) => ({
            ...previous,

            invoiceItems:
                previous.invoiceItems.map(
                    (item, itemIndex) =>
                        itemIndex === index
                            ? calculateInvoiceItem({
                                ...item,
                                [field]: value,
                            })
                            : item,
                ),
        }));
    };

    const handleCustomerCreated = (
        createdCustomer,
    ) => {
        const attributes =
            createdCustomer?.attributes ||
            createdCustomer ||
            {};

        const normalizedCustomer = {
            ...attributes,
            ...createdCustomer,
            _id:
                createdCustomer?._id ||
                createdCustomer?.id ||
                attributes?._id ||
                attributes?.id,
            id:
                createdCustomer?.id ||
                createdCustomer?._id ||
                attributes?.id ||
                attributes?._id,
            name:
                attributes?.name ||
                createdCustomer?.name ||
                "Unnamed Customer",
            type: "customer",
        };

        setCustomers((previous) => {
            const exists = previous.some(
                (customer) =>
                    String(getPartyId(customer)) ===
                    String(
                        getPartyId(
                            normalizedCustomer,
                        ),
                    ),
            );

            return exists
                ? previous
                : [
                    ...previous,
                    normalizedCustomer,
                ];
        });

        handleCustomerChange(
            null,
            normalizedCustomer,
        );
        setCustomerModalOpen(false);
        setCustomerOptionsOpen(false);
    };

    const handleInvoiceItemSelection = (
        index,
        value,
    ) => {
        const selectedOption =
            invoiceItemOptions.find(
                (option) =>
                    option.label === value,
            );

        setForm((previous) => ({
            ...previous,
            invoiceItems:
                previous.invoiceItems.map(
                    (item, itemIndex) =>
                        itemIndex === index
                            ? calculateInvoiceItem({
                                ...item,
                                commodity:
                                    selectedOption?.label ||
                                    "",
                                itemCode:
                                    selectedOption?.code ||
                                    "",
                                description:
                                    selectedOption?.description ||
                                    "",
                            })
                            : item,
                ),
        }));
    };

    const addInvoiceItem = () => {
        setForm((previous) => ({
            ...previous,

            invoiceItems: [
                ...previous.invoiceItems,

                createEmptyInvoiceItem(
                    previous
                        .sellingRateCurrency,
                    previous.costCurrency,
                ),
            ],
        }));
    };

    const duplicateInvoiceItem = (
        index,
    ) => {
        setForm((previous) => {
            const selectedItem =
                previous.invoiceItems[index];

            return {
                ...previous,

                invoiceItems: [
                    ...previous.invoiceItems.slice(
                        0,
                        index + 1,
                    ),

                    calculateInvoiceItem({
                        ...selectedItem,
                    }),

                    ...previous.invoiceItems.slice(
                        index + 1,
                    ),
                ],
            };
        });
    };

    const removeInvoiceItem = (
        index,
    ) => {
        setForm((previous) => {
            if (
                previous.invoiceItems.length ===
                1
            ) {
                return previous;
            }

            return {
                ...previous,

                invoiceItems:
                    previous.invoiceItems.filter(
                        (
                            item,
                            itemIndex,
                        ) =>
                            itemIndex !==
                            index,
                    ),
            };
        });
    };

    const applySellingCurrency = (
        currency,
    ) => {
        const normalized =
            normalizeCurrency(currency);

        setForm((previous) => ({
            ...previous,

            currency: normalized,

            sellingRateCurrency:
            normalized,

            invoiceItems:
                previous.invoiceItems.map(
                    (item) => ({
                        ...item,

                        sellingRateCurrency:
                        normalized,
                    }),
                ),
        }));
    };

    const applyCostCurrency = (
        currency,
    ) => {
        const normalized =
            normalizeCurrency(currency);

        setForm((previous) => ({
            ...previous,

            costCurrency: normalized,

            totalCostCurrency:
            normalized,

            invoiceItems:
                previous.invoiceItems.map(
                    (item) => ({
                        ...item,

                        costCurrency:
                        normalized,
                    }),
                ),
        }));
    };

    const validateForm = () => {
        if (
            form.invoiceType === "sales" &&
            !form.customerName.trim()
        ) {
            return financeText("Customer is required.");
        }

        if (
            form.invoiceType === "purchase" &&
            !form.supplierName.trim()
        ) {
            return financeText("Supplier is required.");
        }

        if (!form.invoiceDate) {
            return financeText("Invoice date is required.");
        }

        if (!form.invoiceItems.length) {
            return financeText("At least one invoice item is required.");
        }

        const invalidItem =
            form.invoiceItems.find(
                (item) =>
                    !String(
                        item.commodity || "",
                    ).trim() ||
                    toNumber(
                        item.unitQuantity,
                    ) <= 0,
            );

        if (invalidItem) {
            return financeText("Every invoice item must have a name and quantity.");
        }

        if (totals.invoiceTotal <= 0) {
            return financeText("Invoice total must be greater than zero.");
        }

        return "";
    };

    const buildPayload = (
        saveAndApprove,
    ) => {
        const normalizedItems =
            form.invoiceItems.map(
                calculateInvoiceItem,
            );

        const collectionRisk =
            normalizeCollectionRisk(form);

        return {
            ...form,

            collectionRisk,
            riskStatus: collectionRisk.status,

            invoiceOrigin: "manual",

            invoiceType:
            form.invoiceType,

            transportMode: "none",
            sourceType: "",

            shipmentId: "",
            shipmentNo: "",

            currency:
                normalizeCurrency(
                    form.sellingRateCurrency,
                ),

            sellingRateCurrency:
                normalizeCurrency(
                    form.sellingRateCurrency,
                ),

            costCurrency:
                normalizeCurrency(
                    form.costCurrency,
                ),

            totalCostCurrency:
                normalizeCurrency(
                    form.costCurrency,
                ),

            exchangeRate:
                toNumber(
                    form.exchangeRate,
                ) || 1,

            discountAmount:
            totals.discountAmount,

            ...(() => {
                if (
                    form.invoiceType !== "sales" ||
                    !normalizedItems.some(
                        (item) => toNumber(item.vatRate) === 0,
                    )
                ) {
                    return {
                        exemptionCode: "",
                        exemptionReason: "",
                    };
                }

                const selectedOption = exemptionOptions.find(
                    (option) => option.code === form.exemptionCode,
                );

                return {
                    exemptionCode:
                        selectedOption?.code ||
                        form.exemptionCode ||
                        "311",
                    exemptionReason: selectedOption
                        ? `${selectedOption.code} - ${selectedOption.label}`
                        : form.exemptionReason ||
                        "311 - 14/1 Uluslararası Taşımacılık",
                };
            })(),

            invoiceItems:
            normalizedItems,

            invoiceSalesAmount:
            totals.subtotal,

            actualCostTotal:
            totals.totalCost,

            expectedCostTotal:
            totals.totalCost,

            totalCost:
            totals.totalCost,

            totalCostIncludesBuyingRate:
                true,

            vatAmount:
            totals.vatTotal,

            totalAmount:
            totals.invoiceTotal,

            balanceAmount:
            totals.invoiceTotal,

            saveAndApprove,

            // "Save Draft" used to unconditionally force invoiceStatus back
            // to "Draft" — fine for a brand-new invoice, but editing an
            // already-Issued/Received invoice (e.g. to fix a typo) and
            // clicking the non-primary button silently reverted it to
            // Draft. Once finalized, a plain save keeps the current status;
            // only the explicit "approve" action still advances it.
            invoiceStatus: saveAndApprove
                ? "Issued"
                : ["Issued", "Received"].includes(form.invoiceStatus)
                    ? form.invoiceStatus
                    : "Draft",

            approvalStatus: saveAndApprove
                ? "Approved"
                : ["Issued", "Received"].includes(form.invoiceStatus)
                    ? form.approvalStatus
                    : "Pending",
        };
    };

    const saveInvoice = async (
        saveAndApprove = false,
    ) => {
        const validationMessage =
            validateForm();

        if (validationMessage) {
            setErrorMessage(
                validationMessage,
            );

            return;
        }

        try {
            setLoading(true);
            setErrorMessage("");
            setSuccessMessage("");

            const payload =
                buildPayload(
                    saveAndApprove,
                );

            const path = isEditMode
                ? `/finance/manual-invoices/${invoiceId}`
                : "/finance/manual-invoices";

            const method = isEditMode
                ? "PATCH"
                : "POST";

            const responsePayload =
                await fetchWithAuth(path, {
                    method,

                    body: JSON.stringify(
                        payload,
                    ),
                });

            const savedInvoice =
                extractRecord(
                    responsePayload,
                );

            const savedId =
                savedInvoice?._id ||
                savedInvoice?.id ||
                invoiceId;

            // The invoice is stored first; the PDF upload and the accounting
            // mail follow. If either fails the invoice is NOT lost: the user is
            // told and can retry from the invoice's edit page.
            let mailedTo = [];
            let followUpError = "";

            const hasStoredPdf = Boolean(form.foreignInvoicePdf?.fileName);
            const alreadyMailed =
                Boolean(form.foreignInvoiceMail?.sentAt) && !pdfFile;

            try {
                if (pdfFile && savedId) {
                    await uploadInvoicePdf(savedId, pdfFile);
                }

                if (
                    sendPdfMail &&
                    savedId &&
                    (pdfFile || hasStoredPdf) &&
                    !alreadyMailed
                ) {
                    const mailResult = await mailInvoicePdf(savedId);

                    mailedTo = mailResult?.data?.mail?.to || [];
                }
            } catch (followUpFailure) {
                followUpError = followUpFailure.message;
            }

            const baseMessage = financeText(
                saveAndApprove
                    ? "Invoice saved and approved successfully."
                    : "Invoice draft saved successfully.",
            );

            if (followUpError) {
                appAlert(
                    `${baseMessage}\n\n${financeText("But the PDF step failed")}: ${followUpError}\n${financeText("Open the invoice and try again.")}`,
                );
            }

            setSuccessMessage(
                mailedTo.length
                    ? `${baseMessage} ${financeText("PDF e-mailed to")} ${mailedTo.join(", ")}.`
                    : baseMessage,
            );

            setTimeout(() => {
                if (followUpError && !isEditMode && savedId) {
                    navigate(
                        `/invoice/finance/manual-invoices/${savedId}/edit`,
                    );

                    return;
                }

                navigate(
                    form.invoiceType ===
                    "purchase"
                        ? "/invoice/finance/supplier-invoices"
                        : "/invoice/finance/customer-invoices",
                    {
                        state: {
                            createdInvoiceId: savedId,
                        },
                    },
                );
            }, mailedTo.length ? 1800 : 500);
        } catch (error) {
            console.error(
                "Manual invoice save error:",
                error,
            );

            setErrorMessage(
                error.message ||
                financeText("Manual invoice could not be saved."),
            );
        } finally {
            setLoading(false);
        }
    };

    const cancelPage = () => {
        navigate(
            form.invoiceType === "purchase"
                ? "/invoice/finance/supplier-invoices"
                : "/invoice/finance/customer-invoices",
        );
    };

    if (loadingInvoice) {
        return (
            <DashboardLayout>
                <DashboardNavbar />

                <MDBox
                    minHeight="70vh"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CircularProgress />
                </MDBox>

                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background:
                    COLORS.page,

                    mx: {
                        xs: -2,
                        md: -3,
                    },

                    px: {
                        xs: 2,
                        md: 3,
                    },
                }}
            >
                <MDBox
                    mb={3}
                    p={{
                        xs: 2.5,
                        md: 3.5,
                    }}
                    borderRadius="20px"
                    sx={{
                        color: "#FFFFFF",

                        background:
                            "linear-gradient(135deg, #0B1F3A 0%, #175CD3 100%)",

                        boxShadow:
                            "0 16px 40px rgba(11,31,58,.16)",
                    }}
                >
                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{
                            xs: "flex-start",
                            md: "center",
                        }}
                        flexDirection={{
                            xs: "column",
                            md: "row",
                        }}
                        gap={2}
                    >
                        <MDBox>
                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={1}
                            >
                                <ReceiptLong />

                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",

                                        letterSpacing:
                                            ".1em",
                                    }}
                                >
                                    {financeText("MANUAL FINANCE OPERATIONS")}
                                </MDTypography>
                            </MDBox>

                            <MDTypography
                                variant="h3"
                                fontWeight="bold"
                                sx={{
                                    color:
                                        "#FFFFFF",
                                }}
                            >
                                {financeText(
                                    isEditMode
                                        ? "Edit Manual Invoice"
                                        : "Create Manual Invoice"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="body2"
                                mt={1}
                                sx={{
                                    color:
                                        "rgba(255,255,255,.72)",
                                }}
                            >
                                {financeText(
                                    isEditMode
                                        ? "Update this manual invoice, its attached PDF and its notes."
                                        : "Create a sales or purchase invoice without a TMS shipment."
                                )}
                            </MDTypography>
                        </MDBox>

                        <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                        >
                            <MDBox
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    p: "4px",
                                    background: "rgba(255,255,255,.10)",
                                    border: "1px solid rgba(255,255,255,.20)",
                                    borderRadius: "12px",
                                    boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                }}
                            >
                                <MDBox
                                    component="button"
                                    type="button"
                                    onClick={() =>
                                        changeFinanceLanguage({
                                            target: { value: "en" },
                                        })
                                    }
                                    title="English"
                                    sx={{
                                        appearance: "none",
                                        WebkitAppearance: "none",
                                        border: "none",
                                        outline: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "7px",
                                        height: 38,
                                        minWidth: 76,
                                        px: 1.15,
                                        borderRadius: "8px",
                                        background:
                                            financeLanguage === "en"
                                                ? "#FFFFFF"
                                                : "transparent",
                                        color:
                                            financeLanguage === "en"
                                                ? COLORS.navy
                                                : "#FFFFFF",
                                        boxShadow:
                                            financeLanguage === "en"
                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                : "none",
                                    }}
                                >
                                    <MDBox
                                        component="img"
                                        src="/flags/gb.svg"
                                        alt="English"
                                        sx={{
                                            width: 24,
                                            height: 17,
                                            objectFit: "cover",
                                            borderRadius: "3px",
                                            display: "block",
                                        }}
                                    />
                                    <MDTypography
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 800,
                                            color: "inherit",
                                            lineHeight: 1,
                                        }}
                                    >
                                        EN
                                    </MDTypography>
                                </MDBox>

                                <MDBox
                                    component="button"
                                    type="button"
                                    onClick={() =>
                                        changeFinanceLanguage({
                                            target: { value: "tr" },
                                        })
                                    }
                                    title="Türkçe"
                                    sx={{
                                        appearance: "none",
                                        WebkitAppearance: "none",
                                        border: "none",
                                        outline: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "7px",
                                        height: 38,
                                        minWidth: 76,
                                        px: 1.15,
                                        borderRadius: "8px",
                                        background:
                                            financeLanguage === "tr"
                                                ? "#FFFFFF"
                                                : "transparent",
                                        color:
                                            financeLanguage === "tr"
                                                ? COLORS.navy
                                                : "#FFFFFF",
                                        boxShadow:
                                            financeLanguage === "tr"
                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                : "none",
                                    }}
                                >
                                    <MDBox
                                        component="img"
                                        src="/flags/tr.svg"
                                        alt="Türkçe"
                                        sx={{
                                            width: 24,
                                            height: 17,
                                            objectFit: "cover",
                                            borderRadius: "3px",
                                            display: "block",
                                        }}
                                    />
                                    <MDTypography
                                        component="span"
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 800,
                                            color: "inherit",
                                            lineHeight: 1,
                                        }}
                                    >
                                        TR
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <Chip
                                label={financeText(
                                    form.invoiceType ===
                                    "sales"
                                        ? "Sales Invoice"
                                        : "Purchase Invoice"
                                )}
                                sx={{
                                    color: "#FFFFFF",

                                    background:
                                        "rgba(255,255,255,.12)",

                                    border:
                                        "1px solid rgba(255,255,255,.18)",

                                    fontWeight: 700,
                                }}
                            />
                        </MDBox>
                    </MDBox>
                </MDBox>

                {errorMessage ? (
                    <Alert
                        severity="error"
                        onClose={() =>
                            setErrorMessage("")
                        }
                        sx={{
                            mb: 2,
                            borderRadius: "8px",
                        }}
                    >
                        {errorMessage}
                    </Alert>
                ) : null}

                {successMessage ? (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 2,
                            borderRadius: "8px",
                        }}
                    >
                        {successMessage}
                    </Alert>
                ) : null}

                {itemsLocked ? (
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            borderRadius: "8px",
                        }}
                    >
                        {financeText(
                            "This invoice is already issued. Its line items, discount and currencies are locked; you can still update dates, notes, addresses and the attached PDF."
                        )}
                    </Alert>
                ) : null}

                <Card sx={cardSx}>
                    <MDBox p={3}>
                        <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={2.5}
                        >
                            <Description
                                sx={{
                                    color:
                                    COLORS.blue,
                                }}
                            />

                            <MDBox>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Invoice Information")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText("General information and invoice party details")}
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Type")}
                                    disabled={isEditMode}
                                    value={
                                        form.invoiceType
                                    }
                                    onChange={
                                        handleInvoiceTypeChange
                                    }
                                >
                                    {invoiceTypeOptions.map(
                                        (option) => (
                                            <MenuItem
                                                key={
                                                    option.value
                                                }
                                                value={
                                                    option.value
                                                }
                                            >
                                                {financeText(option.label)}
                                            </MenuItem>
                                        ),
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice No")}
                                    value={
                                        form.invoiceNo
                                    }
                                    placeholder={financeText("Generated automatically")}
                                    disabled
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Invoice Date")}
                                    value={
                                        form.invoiceDate
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "invoiceDate",
                                            event.target
                                                .value,
                                        )
                                    }
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Due Date")}
                                    value={
                                        form.dueDate
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "dueDate",
                                            event.target
                                                .value,
                                        )
                                    }
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                {form.invoiceType ===
                                "sales" ? (
                                    <Autocomplete
                                        key="party-customer"
                                        loading={
                                            loadingParties
                                        }
                                        open={
                                            customerOptionsOpen
                                        }
                                        onOpen={() =>
                                            setCustomerOptionsOpen(
                                                true,
                                            )
                                        }
                                        onClose={() =>
                                            setCustomerOptionsOpen(
                                                false,
                                            )
                                        }
                                        options={[
                                            NEW_CUSTOMER_OPTION,
                                            ...customers,
                                        ]}
                                        value={
                                            selectedCustomer
                                        }
                                        inputValue={
                                            customerInputValue
                                        }
                                        onChange={(
                                            event,
                                            customer,
                                        ) => {
                                            if (
                                                customer?.type ===
                                                "new"
                                            ) {
                                                setCustomerModalOpen(
                                                    true,
                                                );
                                                setCustomerOptionsOpen(
                                                    false,
                                                );
                                                return;
                                            }

                                            handleCustomerChange(
                                                event,
                                                customer,
                                            );
                                            setCustomerOptionsOpen(
                                                false,
                                            );
                                        }}
                                        onInputChange={(
                                            event,
                                            value,
                                            reason,
                                        ) => {
                                            setCustomerInputValue(
                                                value,
                                            );

                                            if (
                                                reason ===
                                                "input" ||
                                                reason ===
                                                "clear"
                                            ) {
                                                setCustomerOptionsOpen(
                                                    value
                                                        .trim()
                                                        .length >
                                                    0,
                                                );
                                            }

                                            if (
                                                reason ===
                                                "clear"
                                            ) {
                                                handleCustomerChange(
                                                    event,
                                                    null,
                                                );
                                            }
                                        }}
                                        getOptionLabel={(
                                            option,
                                        ) =>
                                            getPartyName(
                                                option,
                                            )
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value,
                                        ) =>
                                            String(
                                                getPartyId(
                                                    option,
                                                ),
                                            ) ===
                                            String(
                                                getPartyId(
                                                    value,
                                                ),
                                            ) &&
                                            String(
                                                getPartyType(
                                                    option,
                                                ),
                                            ) ===
                                            String(
                                                getPartyType(
                                                    value,
                                                ),
                                            )
                                        }
                                        renderOption={(
                                            { key: optionKey, ...props },
                                            option,
                                        ) => (
                                            <li
                                                {...props}
                                                key={`${getPartyType(option)}-${getPartyId(option)}`}
                                            >
                                                {option?.type ===
                                                "new" ? (
                                                    <strong
                                                        style={{
                                                            color:
                                                            COLORS.blue,
                                                        }}
                                                    >
                                                        {financeText("+ New Account")}
                                                    </strong>
                                                ) : (
                                                    <>
                                                        {getPartyName(
                                                            option,
                                                        )}
                                                        <span
                                                            style={{
                                                                marginLeft: 8,
                                                                opacity: 0.7,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            ({financeText(
                                                            getPartyTypeLabel(option),
                                                        )})
                                                        </span>
                                                    </>
                                                )}
                                            </li>
                                        )}
                                        renderInput={(
                                            params,
                                        ) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label={financeText(
                                                    isEditMode
                                                        ? "Selected Account"
                                                        : "Search Customer / Service Provider / Global Agent"
                                                )}
                                                placeholder={financeText("Type to search or create an account")}
                                                required
                                            />
                                        )}
                                    />
                                ) : (
                                    <Autocomplete
                                        key="party-supplier"
                                        loading={
                                            loadingParties
                                        }
                                        options={
                                            suppliers
                                        }
                                        value={
                                            selectedSupplier
                                        }
                                        onChange={
                                            handleSupplierChange
                                        }
                                        getOptionLabel={(
                                            option,
                                        ) =>
                                            getPartyName(
                                                option,
                                            )
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value,
                                        ) =>
                                            String(
                                                getPartyId(
                                                    option,
                                                ),
                                            ) ===
                                            String(
                                                getPartyId(
                                                    value,
                                                ),
                                            )
                                        }
                                        renderInput={(
                                            params,
                                        ) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label={financeText("Supplier")}
                                                placeholder={financeText("Select supplier")}
                                            />
                                        )}
                                    />
                                )}
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={financeText("Email")}
                                    value={
                                        form.email
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "email",
                                            event.target
                                                .value,
                                        )
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={financeText("Exchange Rate")}
                                    type="number"
                                    value={
                                        form.exchangeRate
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "exchangeRate",
                                            event.target
                                                .value,
                                        )
                                    }
                                    inputProps={{
                                        min: 0,
                                        step: "0.0001",
                                    }}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Selling Currency")}
                                    disabled={itemsLocked}
                                    value={
                                        form.sellingRateCurrency
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        applySellingCurrency(
                                            event.target
                                                .value,
                                        )
                                    }
                                >
                                    {currencyOptions.map(
                                        (currency) => (
                                            <MenuItem
                                                key={
                                                    currency
                                                }
                                                value={
                                                    currency
                                                }
                                            >
                                                {
                                                    currency
                                                }
                                            </MenuItem>
                                        ),
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Cost Currency")}
                                    disabled={itemsLocked}
                                    value={
                                        form.costCurrency
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        applyCostCurrency(
                                            event.target
                                                .value,
                                        )
                                    }
                                >
                                    {currencyOptions.map(
                                        (currency) => (
                                            <MenuItem
                                                key={
                                                    currency
                                                }
                                                value={
                                                    currency
                                                }
                                            >
                                                {
                                                    currency
                                                }
                                            </MenuItem>
                                        ),
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Expense Center")}
                                    value={
                                        form.expenseCenter
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "expenseCenter",
                                            event.target
                                                .value,
                                        )
                                    }
                                >
                                    <MenuItem value="">
                                        {financeText("Select Expense Center")}
                                    </MenuItem>

                                    {form.expenseCenter &&
                                    !expenseCenterOptions.includes(
                                        form.expenseCenter,
                                    ) ? (
                                        <MenuItem value={form.expenseCenter}>
                                            {form.expenseCenter}
                                        </MenuItem>
                                    ) : null}

                                    {expenseCenterOptions.map(
                                        (option) => (
                                            <MenuItem
                                                key={option}
                                                value={option}
                                            >
                                                {financeText(option)}
                                            </MenuItem>
                                        ),
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={financeText("Branch")}
                                    value={
                                        form.branch
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "branch",
                                            event.target
                                                .value,
                                        )
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Operation")}
                                    value={
                                        form.operation
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "operation",
                                            event.target
                                                .value,
                                        )
                                    }
                                >
                                    <MenuItem value="">
                                        {financeText("Select Operation")}
                                    </MenuItem>

                                    {form.operation &&
                                    !operationOptions.includes(
                                        form.operation,
                                    ) ? (
                                        <MenuItem value={form.operation}>
                                            {form.operation}
                                        </MenuItem>
                                    ) : null}

                                    {operationOptions.map(
                                        (option) => (
                                            <MenuItem
                                                key={option}
                                                value={option}
                                            >
                                                {financeText(option)}
                                            </MenuItem>
                                        ),
                                    )}
                                </TextField>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={
                                        salesRepresentativeOptions
                                    }
                                    value={
                                        salesRepresentativeOptions.find(
                                            (option) =>
                                                option.label ===
                                                form.salesRepresentative,
                                        ) || null
                                    }
                                    isOptionEqualToValue={(
                                        option,
                                        value,
                                    ) =>
                                        String(option.id) ===
                                        String(value.id)
                                    }
                                    onChange={(
                                        event,
                                        option,
                                    ) =>
                                        updateFormField(
                                            "salesRepresentative",
                                            option?.label || "",
                                        )
                                    }
                                    renderInput={(
                                        params,
                                    ) => (
                                        <TextField
                                            {...params}
                                            label={financeText("Sales Representative")}
                                            placeholder={financeText("Select sales representative")}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label={financeText("Invoice Address")}
                                    value={
                                        form.invoiceAddress
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "invoiceAddress",
                                            event.target
                                                .value,
                                        )
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label={financeText("Notes")}
                                    value={
                                        form.notes
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "notes",
                                            event.target
                                                .value,
                                        )
                                    }
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card
                    aria-disabled={itemsLocked || undefined}
                    sx={{
                        ...cardSx,
                        mt: 2.5,
                        ...(itemsLocked && {
                            pointerEvents: "none",
                            opacity: 0.7,
                            userSelect: "none",
                        }),
                    }}
                >
                    <MDBox p={3}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={1.5}
                            mb={2.5}
                        >
                            <MDBox>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Invoice Line Items")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText("Total cost remains internal and is not displayed on the customer PDF.")}
                                </MDTypography>
                            </MDBox>

                            <AddRowButton onClick={
                                    addInvoiceItem
                                } tooltip="Add invoice item" disabled={itemsLocked} />
                        </MDBox>

                        <MDBox
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            {form.invoiceItems.map(
                                (
                                    item,
                                    index,
                                ) => (
                                    <Paper
                                        key={`invoice-item-${index}`}
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            borderRadius:
                                                "8px",

                                            borderColor:
                                            COLORS.border,
                                        }}
                                    >
                                        <Grid
                                            container
                                            spacing={1.5}
                                            alignItems="center"
                                        >
                                            <Grid
                                                item
                                                xs={12}
                                                md={1.2}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("Item Code")}
                                                    value={
                                                        item.itemCode
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "itemCode",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                md={1.8}
                                            >
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("Invoice Item")}
                                                    value={
                                                        item.commodity
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        handleInvoiceItemSelection(
                                                            index,
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        {financeText("Select Invoice Item")}
                                                    </MenuItem>

                                                    {item.commodity &&
                                                    !invoiceItemOptions.some(
                                                        (option) =>
                                                            option.label ===
                                                            item.commodity,
                                                    ) ? (
                                                        <MenuItem
                                                            value={item.commodity}
                                                        >
                                                            {item.commodity}
                                                        </MenuItem>
                                                    ) : null}

                                                    {invoiceItemOptions.map(
                                                        (option) => (
                                                            <MenuItem
                                                                key={option.code}
                                                                // The value is the canonical (English) label that is
                                                                // stored on the invoice and matched in
                                                                // handleInvoiceItemSelection; only the text shown is
                                                                // translated. A translated value never matched, so in
                                                                // Turkish nothing could be selected.
                                                                value={option.label}
                                                            >
                                                                {financeText(option.label)}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </TextField>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                md={2}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("Description")}
                                                    value={
                                                        item.description
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "description",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                                md={0.8}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    label={financeText("Qty")}
                                                    value={
                                                        item.unitQuantity
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "unitQuantity",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                    inputProps={{
                                                        min: 0,
                                                        step: "0.01",
                                                    }}
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                                md={1.2}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    label={financeText("Unit Price")}
                                                    value={
                                                        item.unitPrice
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "unitPrice",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                    inputProps={{
                                                        min: 0,
                                                        step: "0.01",
                                                    }}
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                                md={1}
                                            >
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("Sales Curr.")}
                                                    value={
                                                        item.sellingRateCurrency
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "sellingRateCurrency",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                >
                                                    {currencyOptions.map(
                                                        (
                                                            currency,
                                                        ) => (
                                                            <MenuItem
                                                                key={
                                                                    currency
                                                                }
                                                                value={
                                                                    currency
                                                                }
                                                            >
                                                                {
                                                                    currency
                                                                }
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </TextField>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                                md={1.1}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    label={financeText("Total Cost")}
                                                    value={
                                                        item.totalCost
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "totalCost",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                    inputProps={{
                                                        min: 0,
                                                        step: "0.01",
                                                    }}
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                                md={1}
                                            >
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("Cost Curr.")}
                                                    value={
                                                        item.costCurrency
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "costCurrency",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                >
                                                    {currencyOptions.map(
                                                        (
                                                            currency,
                                                        ) => (
                                                            <MenuItem
                                                                key={
                                                                    currency
                                                                }
                                                                value={
                                                                    currency
                                                                }
                                                            >
                                                                {
                                                                    currency
                                                                }
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </TextField>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={6}
                                                md={0.9}
                                            >
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("VAT")}
                                                    value={
                                                        item.vatRate
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "vatRate",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                >
                                                    {getVatOptionsFor(
                                                        form.invoiceType,
                                                        item.vatRate,
                                                    ).map(
                                                        (
                                                            vatRate,
                                                        ) => (
                                                            <MenuItem
                                                                key={
                                                                    vatRate
                                                                }
                                                                value={
                                                                    vatRate
                                                                }
                                                            >
                                                                {`%${vatRate}`}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </TextField>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={8}
                                                md={1.3}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("Amount")}
                                                    value={formatMoney(
                                                        item.totalWithVat,
                                                        item.sellingRateCurrency,
                                                    )}
                                                    disabled
                                                />
                                            </Grid>

                                            <Grid
                                                item
                                                xs={4}
                                                md={0.7}
                                            >
                                                <MDBox
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                    gap={0.5}
                                                >
                                                    <Tooltip title={financeText("Duplicate item")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                duplicateInvoiceItem(
                                                                    index,
                                                                )
                                                            }
                                                            sx={{
                                                                color:
                                                                COLORS.purple,

                                                                background:
                                                                COLORS.purpleLight,
                                                            }}
                                                        >
                                                            <ContentCopy fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={financeText("Delete item")}>
                                                        <span>
                                                            <RemoveRowButton onClick={() =>
                                                                    removeInvoiceItem(
                                                                        index,
                                                                    )
                                                                } tooltip="Remove invoice item" disabled={
                                                                    form
                                                                        .invoiceItems
                                                                        .length ===
                                                                    1
                                                                } />
                                                        </span>
                                                    </Tooltip>
                                                </MDBox>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                md={3}
                                            >
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label={financeText("Expense Center")}
                                                    value={
                                                        item.expenseCenter
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) =>
                                                        updateInvoiceItem(
                                                            index,
                                                            "expenseCenter",
                                                            event
                                                                .target
                                                                .value,
                                                        )
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        {financeText("Select Expense Center")}
                                                    </MenuItem>

                                                    {item.expenseCenter &&
                                                    !expenseCenterOptions.includes(
                                                        item.expenseCenter,
                                                    ) ? (
                                                        <MenuItem
                                                            value={
                                                                item.expenseCenter
                                                            }
                                                        >
                                                            {item.expenseCenter}
                                                        </MenuItem>
                                                    ) : null}

                                                    {expenseCenterOptions.map(
                                                        (option) => (
                                                            <MenuItem
                                                                key={option}
                                                                value={option}
                                                            >
                                                                {option}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </TextField>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                md={9}
                                            >
                                                <MDBox
                                                    display="flex"
                                                    flexWrap="wrap"
                                                    gap={1}
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={`${financeText("Subtotal")}: ${formatMoney(
                                                            item.amount,
                                                            item.sellingRateCurrency,
                                                        )}`}
                                                    />

                                                    <Chip
                                                        size="small"
                                                        label={`${financeText("VAT")}: ${formatMoney(
                                                            item.vatAmount,
                                                            item.sellingRateCurrency,
                                                        )}`}
                                                    />

                                                    <Chip
                                                        size="small"
                                                        label={`${financeText("Internal Cost")}: ${formatMoney(
                                                            item.totalCost,
                                                            item.costCurrency,
                                                        )}`}
                                                        sx={{
                                                            color:
                                                            COLORS.red,

                                                            background:
                                                            COLORS.redLight,
                                                        }}
                                                    />
                                                </MDBox>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ),
                            )}
                        </MDBox>
                    </MDBox>
                </Card>

                <Card
                    sx={{
                        ...cardSx,
                        mt: 2.5,
                    }}
                >
                    <MDBox p={3}>
                        <MDTypography
                            variant="h6"
                            fontWeight="bold"
                            sx={{ color: COLORS.text }}
                        >
                            {financeText("Foreign Invoice PDF")}
                        </MDTypography>

                        <MDTypography
                            variant="caption"
                            display="block"
                            mb={2}
                            sx={{ color: COLORS.subtext }}
                        >
                            {financeText(
                                "Attach the foreign invoice as a PDF and e-mail it to accounting to be processed.",
                            )}
                        </MDTypography>

                        {isEditMode && form.foreignInvoicePdf?.fileName && (
                            <MDBox
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1}
                                mb={2}
                                p={1.5}
                                sx={{
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: "8px",
                                    background: COLORS.muted || "#F9FAFB",
                                }}
                            >
                                <MDTypography variant="button" fontWeight="bold">
                                    {form.foreignInvoicePdf.fileName}
                                </MDTypography>

                                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                    {Math.max(
                                        1,
                                        Math.round((form.foreignInvoicePdf.size || 0) / 1024),
                                    )}{" "}
                                    KB
                                </MDTypography>

                                <MDBox flexGrow={1} />

                                <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    disabled={pdfBusy}
                                    onClick={viewSavedPdf}
                                >
                                    {financeText("View")}
                                </MDButton>

                                <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={pdfBusy}
                                    onClick={removeSavedPdf}
                                >
                                    {financeText("Remove")}
                                </MDButton>
                            </MDBox>
                        )}

                        <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                            <MDButton
                                component="label"
                                variant="outlined"
                                color="dark"
                                size="small"
                            >
                                {pdfFile || form.foreignInvoicePdf?.fileName
                                    ? financeText("Replace PDF")
                                    : financeText("Choose PDF")}
                                <input
                                    hidden
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    onChange={handlePdfChosen}
                                />
                            </MDButton>

                            {pdfFile && (
                                <Chip
                                    size="small"
                                    label={`${pdfFile.name} (${Math.max(1, Math.round(pdfFile.size / 1024))} KB)`}
                                    onDelete={() => setPdfFile(null)}
                                    sx={{ maxWidth: 360 }}
                                />
                            )}
                        </MDBox>

                        <MDBox mt={1.5}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendPdfMail}
                                        onChange={(event) =>
                                            setSendPdfMail(event.target.checked)
                                        }
                                    />
                                }
                                label={financeText(
                                    "E-mail the PDF to accounting for processing when saving",
                                )}
                            />

                            <MDBox display="flex" flexWrap="wrap" gap={0.7} ml={4}>
                                {(pdfMailConfig.recipients || []).map((address) => (
                                    <Chip
                                        key={address}
                                        size="small"
                                        label={address}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            fontWeight: 600,
                                        }}
                                    />
                                ))}
                            </MDBox>
                        </MDBox>

                        {isEditMode && form.foreignInvoicePdf?.fileName && (
                            <MDBox
                                mt={2}
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1.5}
                            >
                                {form.foreignInvoiceMail?.sentAt ? (
                                    <Alert severity="success" sx={{ py: 0.2 }}>
                                        {financeText("E-mailed to")}{" "}
                                        {(form.foreignInvoiceMail.to || []).join(", ")} ·{" "}
                                        {new Date(form.foreignInvoiceMail.sentAt).toLocaleString("tr-TR")}
                                        {form.foreignInvoiceMail.count > 1
                                            ? ` (${form.foreignInvoiceMail.count}×)`
                                            : ""}
                                    </Alert>
                                ) : (
                                    <Alert severity="info" sx={{ py: 0.2 }}>
                                        {financeText("Not e-mailed yet")}
                                    </Alert>
                                )}

                                <MDButton
                                    size="small"
                                    variant="gradient"
                                    color="info"
                                    disabled={pdfBusy}
                                    onClick={() =>
                                        sendSavedPdf(Boolean(form.foreignInvoiceMail?.sentAt))
                                    }
                                >
                                    {form.foreignInvoiceMail?.sentAt
                                        ? financeText("Send again")
                                        : financeText("Send to accounting")}
                                </MDButton>
                            </MDBox>
                        )}

                        {pdfNotice && (
                            <Alert severity="success" sx={{ mt: 1.5 }}>
                                {pdfNotice}
                            </Alert>
                        )}
                    </MDBox>
                </Card>

                <Grid
                    container
                    spacing={2.5}
                    mt={0.1}
                >
                    <Grid
                        item
                        xs={12}
                        lg={7}
                    >
                        <Card
                            sx={{
                                ...cardSx,
                                height: "100%",
                            }}
                        >
                            <MDBox p={3}>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    mb={2}
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Additional Information")}
                                </MDTypography>

                                <TextField
                                    fullWidth
                                    type="number"
                                    label={financeText("Discount Amount")}
                                    disabled={itemsLocked}
                                    value={
                                        form.discountAmount
                                    }
                                    onChange={(
                                        event,
                                    ) =>
                                        updateFormField(
                                            "discountAmount",
                                            event.target
                                                .value,
                                        )
                                    }
                                    inputProps={{
                                        min: 0,
                                        step: "0.01",
                                    }}
                                />

                                {hasZeroVatLine && (
                                    <MDBox mt={2.5}>
                                        <Autocomplete
                                            options={exemptionOptions}
                                            getOptionLabel={(option) =>
                                                option
                                                    ? `${option.code} - ${option.label}`
                                                    : ""
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                option.code === value.code
                                            }
                                            value={
                                                exemptionOptions.find(
                                                    (option) =>
                                                        option.code ===
                                                        (form.exemptionCode || "311"),
                                                ) || null
                                            }
                                            onChange={(event, newValue) => {
                                                updateFormField(
                                                    "exemptionCode",
                                                    newValue?.code || "",
                                                );
                                                updateFormField(
                                                    "exemptionReason",
                                                    newValue
                                                        ? `${newValue.code} - ${newValue.label}`
                                                        : "",
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={financeText("VAT Exemption Reason Code (GİB)")}
                                                    helperText={financeText(
                                                        "Applies to the 0% VAT lines. This invoice is sent to EDM as an exempt (İstisna) invoice. Default: 311 - international transport; technology: 223. Use 20% VAT for regular taxable sales.",
                                                    )}
                                                />
                                            )}
                                        />
                                    </MDBox>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        lg={5}
                    >
                        <Card sx={cardSx}>
                            <MDBox p={3}>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    mb={2}
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {financeText("Invoice Summary")}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    mb={1.2}
                                >
                                    <MDTypography
                                        variant="button"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Subtotal")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                    >
                                        {formatMoney(
                                            totals.subtotal,
                                            form.sellingRateCurrency,
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    mb={1.2}
                                >
                                    <MDTypography
                                        variant="button"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("VAT Total")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                    >
                                        {formatMoney(
                                            totals.vatTotal,
                                            form.sellingRateCurrency,
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    mb={1.2}
                                >
                                    <MDTypography
                                        variant="button"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Discount")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.red,
                                        }}
                                    >
                                        -
                                        {formatMoney(
                                            totals.discountAmount,
                                            form.sellingRateCurrency,
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <Divider sx={{ my: 1.5 }} />

                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                    >
                                        {financeText("Invoice Total")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.blue,
                                        }}
                                    >
                                        {formatMoney(
                                            totals.invoiceTotal,
                                            form.sellingRateCurrency,
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <MDBox
                                    mt={2}
                                    p={1.5}
                                    borderRadius="8px"
                                    sx={{
                                        background:
                                        COLORS.redLight,

                                        border:
                                            "1px solid #FECDCA",
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.red,
                                        }}
                                    >
                                        {financeText("Internal cost")}:{" "}
                                        {formatMoney(
                                            totals.totalCost,
                                            form.costCurrency,
                                        )}
                                        . {financeText("This value will not appear on the PDF.")}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>

                <MDBox
                    mt={2.5}
                    display="flex"
                    justifyContent="flex-end"
                    flexWrap="wrap"
                    gap={1}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        startIcon={<Close />}
                        onClick={cancelPage}
                        disabled={loading}
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="outlined"
                        color="info"
                        startIcon={<Preview />}
                        onClick={() =>
                            setPreviewOpen(true)
                        }
                        disabled={loading}
                    >
                        {financeText("Preview")}
                    </MDButton>

                    <MDButton
                        variant="outlined"
                        color="info"
                        startIcon={<Save />}
                        onClick={() =>
                            saveInvoice(false)
                        }
                        disabled={loading}
                    >
                        {financeText(
                            itemsLocked ? "Save Changes" : "Save Draft"
                        )}
                    </MDButton>

                    {itemsLocked ? null : (
                    <MDButton
                        variant="contained"
                        color="info"
                        startIcon={
                            loading ? (
                                <CircularProgress
                                    size={17}
                                    color="inherit"
                                />
                            ) : (
                                <CheckCircle />
                            )
                        }
                        onClick={() =>
                            saveInvoice(true)
                        }
                        disabled={loading}
                        sx={{
                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {financeText(
                            isEditMode
                                ? "Update and Approve"
                                : "Save and Approve"
                        )}
                    </MDButton>
                    )}
                </MDBox>
            </MDBox>

            <Dialog
                open={customerModalOpen}
                onClose={() =>
                    setCustomerModalOpen(false)
                }
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <MDTypography
                            variant="h5"
                            fontWeight="bold"
                        >
                            {financeText("Create Customer")}
                        </MDTypography>

                        <IconButton
                            onClick={() =>
                                setCustomerModalOpen(
                                    false,
                                )
                            }
                        >
                            <Close />
                        </IconButton>
                    </MDBox>
                </DialogTitle>

                <DialogContent dividers>
                    <CreateCustomer
                        isModal
                        onClose={() =>
                            setCustomerModalOpen(
                                false,
                            )
                        }
                        onCreated={
                            handleCustomerCreated
                        }
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={previewOpen}
                onClose={() =>
                    setPreviewOpen(false)
                }
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <MDBox>
                            <MDTypography
                                variant="h5"
                                fontWeight="bold"
                            >
                                {financeText("Invoice Preview")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Total Cost is intentionally hidden from the preview.")}
                            </MDTypography>
                        </MDBox>

                        <IconButton
                            onClick={() =>
                                setPreviewOpen(false)
                            }
                        >
                            <Close />
                        </IconButton>
                    </MDBox>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid
                        container
                        spacing={2}
                        mb={2}
                    >
                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText(
                                    form.invoiceType ===
                                    "sales"
                                        ? "Customer"
                                        : "Supplier"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                display="block"
                            >
                                {form.invoiceType ===
                                "sales"
                                    ? form.customerName
                                    : form.supplierName}
                            </MDTypography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Invoice Date")}
                            </MDTypography>

                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                display="block"
                            >
                                {form.invoiceDate}
                            </MDTypography>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Due Date")}
                            </MDTypography>

                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                display="block"
                            >
                                {form.dueDate || "-"}
                            </MDTypography>
                        </Grid>
                    </Grid>

                    <DataTable
                        table={previewTable}
                        canSearch={false}
                        showTotalEntries={false}
                        entriesPerPage={false}
                        isSorted
                        noEndBorder
                        disableInternalSort
                    />

                    <MDBox
                        mt={3}
                        ml="auto"
                        width={{
                            xs: "100%",
                            md: 360,
                        }}
                    >
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            mb={1}
                        >
                            <MDTypography variant="button">
                                {financeText("Subtotal")}
                            </MDTypography>

                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                            >
                                {formatMoney(
                                    totals.subtotal,
                                    form.sellingRateCurrency,
                                )}
                            </MDTypography>
                        </MDBox>

                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            mb={1}
                        >
                            <MDTypography variant="button">
                                {financeText("VAT")}
                            </MDTypography>

                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                            >
                                {formatMoney(
                                    totals.vatTotal,
                                    form.sellingRateCurrency,
                                )}
                            </MDTypography>
                        </MDBox>

                        <Divider sx={{ my: 1 }} />

                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                            >
                                {financeText("Total")}
                            </MDTypography>

                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.blue,
                                }}
                            >
                                {formatMoney(
                                    totals.invoiceTotal,
                                    form.sellingRateCurrency,
                                )}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogContent>

                <DialogActions>
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() =>
                            setPreviewOpen(false)
                        }
                    >
                        {financeText("Close")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        color="info"
                        onClick={() => {
                            setPreviewOpen(false);
                            saveInvoice(true);
                        }}
                        disabled={loading}
                    >
                        {financeText("Confirm Invoice")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default CreateManualInvoice;